(() => {
  if (customElements.get('eufy-purchase-bar')) return;
  class EufyPurchaseBar extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const start = () => this.start();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true, signal: this.abort.signal });
      else start();
    }
    start() {
      if (this.form) return;
      this.form = document.querySelector(`main form[data-productid="${this.dataset.productId}"][data-type="add-to-cart-form"]`);
      if (!this.form) return;
      this.submitButton = this.form.querySelector('[name="add"]');
      if (!this.submitButton) return;
      this.config = JSON.parse(this.querySelector('[data-purchase-data]').textContent);
      this.add = this.querySelector('[data-purchase-add]');
      this.buy = this.querySelector('[data-purchase-buy]');
      this.error = this.querySelector('[data-purchase-error]');
      const options = { signal: this.abort.signal };
      const refresh = () => queueMicrotask(() => this.refresh());
      this.form.addEventListener('change', refresh, options);
      this.form.addEventListener('input', refresh, options);
      this.observer = new MutationObserver(refresh);
      this.observer.observe(this.form, { subtree: true, attributes: true, attributeFilter: ['value', 'disabled', 'aria-disabled', 'selected'] });
      this.add.addEventListener('click', () => {
        if (!this.validate()) return;
        this.submitButton.click();
      }, options);
      this.buy.addEventListener('click', () => this.checkout(), options);
      this.querySelector('[data-open-options]').addEventListener('click', () => {
        this.form.scrollIntoView({ block: 'center', behavior: this.scrollBehavior() });
        this.form.querySelector('[data-swatch-item], select, input:not([type="hidden"])')?.focus({ preventScroll: true });
      }, options);
      this.hidden = false;
      document.body.classList.add('eufy-purchase-active');
      this.resize = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--eufy-purchase-height', `${Math.ceil(this.getBoundingClientRect().height)}px`);
      });
      this.resize.observe(this);
      this.refresh();
    }
    scrollBehavior() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }
    refresh() {
      if (!this.isConnected) return;
      const id = this.form.querySelector('[name="id"]')?.value;
      const variant = this.config.variants.find(item => String(item.id) === String(id));
      const disabled = !variant?.available || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true';
      this.variant = variant;
      this.add.disabled = disabled || this.busy;
      this.buy.disabled = disabled || this.busy;
      this.add.textContent = this.submitButton.querySelector('.t4s-btn-atc_text')?.textContent.trim() || this.submitButton.textContent.trim();
      this.querySelector('[data-stock-text]').textContent = !variant ? this.config.text.choose : !variant.available ? this.config.text.soldOut : variant.backorder ? this.config.text.preorder : this.config.text.inStock;
      this.querySelector('[data-delivery-text]').hidden = disabled || variant?.backorder;
      this.classList.toggle('is-unavailable', disabled || !!variant?.backorder);
      if (variant) {
        this.querySelector('[data-purchase-price]').textContent = variant.price;
        const compare = this.querySelector('[data-purchase-compare]');
        compare.textContent = variant.compare;
        compare.hidden = !variant.compare;
        this.querySelector('[data-open-options]').textContent = variant.title;
      }
    }
    validate() {
      this.refresh();
      if (this.buy.disabled) return false;
      if (!this.form.checkValidity()) {
        this.form.scrollIntoView({ block: 'center', behavior: this.scrollBehavior() });
        this.form.reportValidity();
        return false;
      }
      this.error.hidden = true;
      return true;
    }
    async checkout() {
      if (!this.validate()) return;
      const formData = new FormData(this.form);
      formData.delete('sections');
      formData.delete('sections_url');
      this.busy = true;
      this.buy.textContent = this.config.text.loading;
      this.setAttribute('aria-busy', 'true');
      this.refresh();
      try {
        const response = await fetch(`${this.dataset.cartUrl}.js`, { method: 'POST', body: formData, headers: { Accept: 'application/json' }, signal: this.abort.signal });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.config.text.error);
        window.location.assign(this.dataset.checkoutUrl);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.error.textContent = error.message || this.config.text.error;
          this.error.hidden = false;
        }
      } finally {
        this.busy = false;
        this.buy.textContent = this.config.text.buyNow;
        this.removeAttribute('aria-busy');
        this.refresh();
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
      document.body.classList.remove('eufy-purchase-active');
      document.documentElement.style.removeProperty('--eufy-purchase-height');
    }
  }
  customElements.define('eufy-purchase-bar', EufyPurchaseBar);
})();
